import React, { useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Search, Plus, Pencil, Trash2, Loader2, Users } from "lucide-react";
import { toast } from "sonner";
import { MultiSelect } from "@/components/ui/multi-select";

function emptyForm() {
  return { nama: "", hp: "", mata_pelajaran_ids: [], is_active: true, user_id: "" };
}

export default function UstadzPage() {
  const { user } = useAuth();
  const canEdit = user.role === "admin" || user.role === "sekretaris";

  const [items, setItems] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [mapelList, setMapelList] = useState([]);
  const [q, setQ] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (activeFilter !== "all") params.is_active = activeFilter === "active";
      const { data } = await api.get("/ustadz", { params });
      setItems(data.items);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [q, activeFilter]);

  const loadUsers = useCallback(async () => {
    if (user.role !== "admin") return;
    try {
      const { data } = await api.get("/users", { params: { role: "ustadz", limit: 100 } });
      setUsersList(data.items || []);
    } catch (_e) { /* ignore */ }
  }, [user.role]);

  const loadMapel = useCallback(async () => {
    try {
      const { data } = await api.get("/mata-pelajaran", { params: { is_active: true } });
      setMapelList(data.items || []);
    } catch (_e) { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadUsers(); }, [loadUsers]);
  useEffect(() => { loadMapel(); }, [loadMapel]);

  const openCreate = () => { setEditing(null); setForm(emptyForm()); setDialogOpen(true); };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      nama: it.nama || "", hp: it.hp || "",
      mata_pelajaran_ids: it.mata_pelajaran_ids || [],
      is_active: it.is_active !== false, user_id: it.user_id || "",
    });
    setDialogOpen(true);
  };

  const mapelOptions = useMemo(
    () => mapelList.map((m) => ({ value: m.id, label: m.nama, group: m.tingkatan || "" })),
    [mapelList]
  );
  const mapelNameById = useMemo(() => {
    const map = {};
    for (const m of mapelList) map[m.id] = m.nama;
    return map;
  }, [mapelList]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.user_id) payload.user_id = null;
      if (editing) {
        await api.patch(`/ustadz/${editing.id}`, payload);
        toast.success("Ustadz diperbarui");
      } else {
        await api.post("/ustadz", payload);
        toast.success("Ustadz ditambahkan");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/ustadz/${deleteTarget.id}`);
      toast.success("Ustadz dihapus");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="ustadz-page">
      <div className="flex flex-col gap-1">
        <div className="text-xs font-semibold text-emerald-700 uppercase tracking-widest">Data</div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">Data Ustadz</h1>
        <p className="text-slate-500 text-sm">Daftar tenaga pengajar Madrasah.</p>
      </div>

      <Card className="p-4 md:p-5 border border-slate-200 shadow-sm bg-white">
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <div className="relative flex-1">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input placeholder="Cari nama ustadz..." value={q} onChange={(e) => setQ(e.target.value)}
                   className="pl-9 h-10" data-testid="ustadz-search-input" />
          </div>
          <Select value={activeFilter} onValueChange={setActiveFilter}>
            <SelectTrigger className="w-full md:w-44 h-10" data-testid="ustadz-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Status</SelectItem>
              <SelectItem value="active">Aktif</SelectItem>
              <SelectItem value="inactive">Nonaktif</SelectItem>
            </SelectContent>
          </Select>
          {canEdit && (
            <Button onClick={openCreate} className="bg-emerald-600 hover:bg-emerald-700 text-white h-10 gap-2"
                    data-testid="add-ustadz-button">
              <Plus className="h-4 w-4" /> Tambah Ustadz
            </Button>
          )}
        </div>
      </Card>

      <Card className="border border-slate-200 shadow-sm bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Nama</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">No. HP</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Mata Pelajaran</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Status</TableHead>
                {canEdit && <TableHead className="text-right text-xs uppercase tracking-wider text-slate-500 font-semibold">Aksi</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} className="text-center py-12 text-slate-400">
                  <Loader2 className="h-5 w-5 mx-auto animate-spin" />
                </TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-12 text-slate-400">
                  Belum ada data ustadz.
                </TableCell></TableRow>
              ) : items.map((it) => (
                <TableRow key={it.id} data-testid={`ustadz-row-${it.id}`} className="hover:bg-emerald-50/40">
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-full bg-amber-50 text-amber-700 flex items-center justify-center">
                        <Users className="h-4 w-4" />
                      </div>
                      <span className="font-semibold text-slate-900">{it.nama}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-700">{it.hp || "-"}</TableCell>
                  <TableCell className="text-sm text-slate-700 max-w-xs">
                    {(it.mata_pelajaran_ids || []).length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {(it.mata_pelajaran_ids || []).map((id) => (
                          <span
                            key={id}
                            className="inline-flex items-center px-2 py-0.5 text-[11px] font-medium rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"
                          >
                            {mapelNameById[id] || "(dihapus)"}
                          </span>
                        ))}
                      </div>
                    ) : it.mata_pelajaran ? (
                      <span className="text-slate-500 italic">{it.mata_pelajaran}</span>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell>
                    <span className={`px-2.5 py-0.5 inline-flex text-xs font-semibold rounded-full ${
                      it.is_active ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"
                    }`}>
                      {it.is_active ? "Aktif" : "Nonaktif"}
                    </span>
                  </TableCell>
                  {canEdit && (
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(it)} data-testid={`edit-ustadz-${it.id}`}>
                        <Pencil className="h-4 w-4 text-slate-600" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(it)} data-testid={`delete-ustadz-${it.id}`}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Ustadz" : "Tambah Ustadz"}</DialogTitle>
            <DialogDescription>Isi informasi ustadz.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <Label>Nama Ustadz *</Label>
              <Input required value={form.nama} onChange={(e) => setForm(f => ({ ...f, nama: e.target.value }))}
                     data-testid="ustadz-form-nama" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>No. HP</Label>
                <Input value={form.hp} onChange={(e) => setForm(f => ({ ...f, hp: e.target.value }))}
                       data-testid="ustadz-form-hp" />
              </div>
              <div className="flex items-center gap-3 pt-6">
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm(f => ({ ...f, is_active: v }))}
                        data-testid="ustadz-form-active" />
                <Label className="!mt-0">Status Aktif</Label>
              </div>
            </div>
            <div>
              <Label>Mata Pelajaran yang Diampu</Label>
              <MultiSelect
                options={mapelOptions}
                value={form.mata_pelajaran_ids}
                onChange={(ids) => setForm(f => ({ ...f, mata_pelajaran_ids: ids }))}
                placeholder="Pilih mata pelajaran (boleh lebih dari satu)..."
                emptyLabel={mapelList.length === 0 ? "Belum ada master mata pelajaran. Tambahkan dulu di menu Mata Pelajaran." : "Tidak ditemukan"}
                testId="ustadz-form-mapel"
              />
              <p className="text-xs text-slate-400 mt-1">
                Daftar diambil dari menu Mata Pelajaran. Penambahan mapel baru tidak memerlukan perubahan kode.
              </p>
            </div>
            {user.role === "admin" && (
              <div>
                <Label>Tautkan ke Akun Pengguna</Label>
                <Select value={form.user_id || "none"} onValueChange={(v) => setForm(f => ({ ...f, user_id: v === "none" ? "" : v }))}>
                  <SelectTrigger data-testid="ustadz-form-user"><SelectValue placeholder="Pilih akun ustadz" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Tidak ditautkan</SelectItem>
                    {usersList.map(u => <SelectItem key={u.id} value={u.id}>{u.name} ({u.email})</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-400 mt-1">Tautan ini memungkinkan ustadz login dan melihat kelas yang diampu.</p>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Batal</Button>
              <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="ustadz-form-submit">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus Ustadz?</AlertDialogTitle>
            <AlertDialogDescription>
              Data <span className="font-semibold">{deleteTarget?.nama}</span> akan dihapus permanen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} data-testid="ustadz-delete-confirm"
                               className="bg-red-600 hover:bg-red-700">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
